import React, {useMemo} from "react";
import {ShopOutlined, UpOutlined, DownOutlined} from "@ant-design/icons";
import {message} from "antd";
import {
    HexagonList as StyledHexagonList,
    HexagonGroup,
    HexagonGroupHeader,
    HexagonItem,
    HexagonCode,
    CompanyCount,
    CompanyList,
    CompanyListHeader,
    CompanyItem
} from "../H3Map.styles";
import {
    HexagonContainer,
    HexagonContent,
    HeaderContainer,
    HeaderText,
    HeaderCount,
    CountText,
    IconWrapper,
    CompanyCountText,
    CompanyIconWrapper,
    CompanyContentWrapper,
    DeleteButton
} from "./HexagonList.styles";

export const HexagonList = ({
    hexagonsByResolution = {},
    resolutionOptions = [],
    markerH3Indexes = {},
    selectedHex,
    expandedSections = {},
    handleHexClick,
    toggleSection,
    toggleCompanyList,
    handleMarkerZoom,
    setResolution,
    disabledResolutions = [],
    hoverHexagon,
    setHoverHexagon,
    onHexagonDelete,
    isActionsEnabled = false
}) => {
    const sortedHexagons = useMemo(() => {
        return Object.entries(hexagonsByResolution)
            .filter(([_, hexagons]) => hexagons?.size > 0)
            .sort(([resA], [resB]) => Number(resB) - Number(resA));
    }, [hexagonsByResolution]);

    function getResolutionLabel(resolution) {
        const option = resolutionOptions.find(opt => opt.value === Number(resolution));
        return option?.label || `${resolution}. Seviye`;
    }

    function renderCompanyList(hex) {
        const markers = markerH3Indexes?.[hex] || [];

        if (!markers.length) {
            return null;
        }

        return (
            <CompanyList>
                <CompanyListHeader>
                    <ShopOutlined style={{fontSize: "14px", color: "#8c8c8c"}} />
                    <span style={{fontSize: "12px", fontWeight: "600", color: "#262626"}}>
                        Firmalar ({markers.length})
                    </span>
                </CompanyListHeader>
                <CompanyContentWrapper>
                    {markers.map((marker, idx) => (
                        <CompanyItem
                            key={marker._id || `marker-${idx}`}
                            onClick={() => handleMarkerZoom?.(marker)}
                        >
                            {marker.name}
                        </CompanyItem>
                    ))}
                </CompanyContentWrapper>
            </CompanyList>
        );
    }

    function handleCompanyCountClick(e, hex) {
        e.stopPropagation();
        toggleCompanyList?.(hex);
    }

    function handleHexagonClick(hex, resolution) {
        if (disabledResolutions.includes(Number(resolution))) {
            message.warning(`${resolution}. seviyede seçim yapılamaz.`);
            return;
        }
        handleHexClick?.(hex, Number(resolution));
        setResolution?.(Number(resolution));
    }

    function handleDeleteHexagon(e, hex, resolution) {
        e.stopPropagation();
        onHexagonDelete?.(hex, Number(resolution));
        setHoverHexagon?.(null);
    }

    return (
        <StyledHexagonList className="hide-scroll">
            {sortedHexagons.map(([resolution, hexagons]) => {
                const isExpanded = expandedSections[resolution];
                const isDisabled = disabledResolutions.includes(Number(resolution));
                const hexagonArray = Array.from(hexagons);

                return (
                    <HexagonGroup key={resolution}>
                        <HexagonGroupHeader
                            onClick={() => toggleSection?.(resolution)}
                            $isExpanded={isExpanded}
                            style={isDisabled ? {opacity: 0.5} : {}}
                        >
                            <HeaderContainer>
                                <HeaderText>{getResolutionLabel(resolution)}</HeaderText>
                            </HeaderContainer>
                            <HeaderCount>
                                <CountText>{hexagons.size}</CountText>
                                <IconWrapper>
                                    {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                </IconWrapper>
                            </HeaderCount>
                        </HexagonGroupHeader>
                        {isExpanded && (
                            <HexagonContent>
                                {hexagonArray.map(hex => {
                                    const markers = markerH3Indexes?.[hex] || [];
                                    const isSelected = selectedHex === hex;
                                    const isDisabled = disabledResolutions.includes(
                                        Number(resolution)
                                    );

                                    return (
                                        <HexagonContainer key={hex}>
                                            <HexagonItem
                                                onClick={() => handleHexagonClick(hex, resolution)}
                                                $isSelected={isSelected}
                                                $isDisabled={isDisabled}
                                                $isHovered={hoverHexagon === hex}
                                                style={isDisabled ? {cursor: "not-allowed"} : {}}
                                                onMouseEnter={() => setHoverHexagon?.(hex)}
                                                onMouseLeave={() => setHoverHexagon?.(null)}
                                            >
                                                <HexagonCode>{hex}</HexagonCode>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "8px",
                                                        alignItems: "center"
                                                    }}
                                                >
                                                    {isActionsEnabled && (
                                                        <DeleteButton
                                                            danger
                                                            size="small"
                                                            onClick={e =>
                                                                handleDeleteHexagon(
                                                                    e,
                                                                    hex,
                                                                    resolution
                                                                )
                                                            }
                                                            $isHovered={hoverHexagon === hex}
                                                        >
                                                            Sil
                                                        </DeleteButton>
                                                    )}
                                                    {markers.length > 0 && isActionsEnabled && (
                                                        <CompanyCount
                                                            onClick={e =>
                                                                handleCompanyCountClick(e, hex)
                                                            }
                                                        >
                                                            <CompanyCountText>
                                                                {markers.length} Firma
                                                            </CompanyCountText>
                                                            <CompanyIconWrapper>
                                                                {isSelected ? (
                                                                    <UpOutlined />
                                                                ) : (
                                                                    <DownOutlined />
                                                                )}
                                                            </CompanyIconWrapper>
                                                        </CompanyCount>
                                                    )}
                                                </div>
                                            </HexagonItem>
                                            {isSelected && renderCompanyList(hex)}
                                        </HexagonContainer>
                                    );
                                })}
                            </HexagonContent>
                        )}
                    </HexagonGroup>
                );
            })}
        </StyledHexagonList>
    );
};
